import axios from 'axios';

/** Mensagens de conflito POST→PUT usadas pela API (compras/tickets e módulo oportunidades). */
export const ALREADY_EXISTS_DETAILS = new Set([
  'Value already exists for this field. Use PUT to update.',
  'Já existe um valor para este campo. Utilize PUT para atualizar.',
]);

export type CustomFieldUpsertArea = 'purchases' | 'tickets' | 'opportunities-module';

export type CustomFieldValuePayload = {
  custom_field_id: number;
  purchase_request_id?: number;
  ticket_id?: number;
  text_value?: string | null;
  document_instance_id?: number | null;
  file_path?: string | null;
  file_paths?: string | null;
  [key: string]: unknown;
};

/** @deprecated Use CustomFieldValuePayload — mantido para callers existentes. */
export type PurchaseCustomFieldValuePayload = CustomFieldValuePayload & {
  purchase_request_id: number;
};

export type UpsertCustomFieldOptions = {
  /** Se true, tenta PUT primeiro; se 404, cria com POST (e trata conflito). */
  knownExists?: boolean;
};

type AxiosLikeError = {
  response?: { status?: number; data?: { detail?: string } };
};

function buildUrls(
  apiUrl: string,
  area: CustomFieldUpsertArea,
  entityId: number,
  customFieldId: number
): { postUrl: string; putUrl: string } {
  if (area === 'opportunities-module') {
    const base = `${apiUrl}/api/purchases/opportunities/module/custom-fields`;
    return {
      postUrl: base,
      putUrl: `${base}/${customFieldId}`,
    };
  }
  const resource = area === 'purchases' ? 'purchases' : 'tickets';
  const base = `${apiUrl}/api/${resource}/${entityId}/custom-fields`;
  return {
    postUrl: base,
    putUrl: `${base}/${customFieldId}`,
  };
}

function stripIdsForPut(valueData: CustomFieldValuePayload): Record<string, unknown> {
  const updateData: Record<string, unknown> = { ...valueData };
  delete updateData.purchase_request_id;
  delete updateData.ticket_id;
  delete updateData.custom_field_id;
  return updateData;
}

function isAlreadyExistsError(error: unknown): boolean {
  const axiosError = error as AxiosLikeError;
  return (
    axiosError.response?.status === 400 &&
    typeof axiosError.response?.data?.detail === 'string' &&
    ALREADY_EXISTS_DETAILS.has(axiosError.response.data.detail)
  );
}

function isNotFoundError(error: unknown): boolean {
  return (error as AxiosLikeError).response?.status === 404;
}

async function postThenPutOnConflict(
  postUrl: string,
  putUrl: string,
  valueData: CustomFieldValuePayload,
  updateData: Record<string, unknown>
): Promise<void> {
  try {
    await axios.post(postUrl, valueData);
  } catch (error: unknown) {
    if (isAlreadyExistsError(error)) {
      await axios.put(putUrl, updateData);
      return;
    }
    throw error;
  }
}

/**
 * Cria ou atualiza valor de campo personalizado (compras, tickets ou módulo oportunidades).
 * Não altera permissões do backend — só normaliza POST/PUT no cliente autenticado.
 *
 * - knownExists: PUT; se 404, POST (com fallback de conflito)
 * - senão: POST; se conflito "already exists", PUT com body limpo
 */
export async function upsertCustomFieldValue(
  apiUrl: string,
  area: CustomFieldUpsertArea,
  entityId: number,
  valueData: CustomFieldValuePayload,
  options?: UpsertCustomFieldOptions
): Promise<void> {
  const customFieldId = Number(valueData.custom_field_id);
  if (!Number.isFinite(customFieldId)) {
    throw new Error('custom_field_id inválido para upsert de campo personalizado.');
  }

  const { postUrl, putUrl } = buildUrls(apiUrl, area, entityId, customFieldId);
  const updateData = stripIdsForPut(valueData);

  if (options?.knownExists) {
    try {
      await axios.put(putUrl, updateData);
      return;
    } catch (error: unknown) {
      if (isNotFoundError(error)) {
        await postThenPutOnConflict(postUrl, putUrl, valueData, updateData);
        return;
      }
      throw error;
    }
  }

  await postThenPutOnConflict(postUrl, putUrl, valueData, updateData);
}

export type CustomFieldBatchItem = {
  custom_field_id: number;
  text_value?: string | null;
  file_path?: string | null;
  file_paths?: string | null;
  document_instance_id?: number | null;
};

export type CustomFieldBatchError = {
  custom_field_id: number;
  detail: string;
};

export type CustomFieldBatchResult = {
  errors: CustomFieldBatchError[];
};

/** True quando a API não expõe a rota em lote (backend anterior a esta versão). */
export function isBatchEndpointUnavailable(error: unknown): boolean {
  const status = (error as AxiosLikeError).response?.status;
  return status === 404 || status === 405;
}

/**
 * Grava vários valores em uma única chamada. A API valida a permissão uma vez e
 * processa os aprovadores só no fim, evitando que o primeiro campo aprovador mova a
 * requisição para "aguardando aprovação" e faça o backend recusar os campos seguintes.
 */
export async function batchUpsertPurchaseCustomFieldValues(
  apiUrl: string,
  purchaseRequestId: number,
  values: CustomFieldBatchItem[]
): Promise<CustomFieldBatchResult> {
  const response = await axios.post(
    `${apiUrl}/api/purchases/${purchaseRequestId}/custom-fields/batch`,
    { values }
  );
  const errors = response?.data?.errors;
  return { errors: Array.isArray(errors) ? errors : [] };
}

/**
 * Cria ou atualiza valor de campo personalizado de compra.
 * POST primeiro; se já existir, PUT sem ids no body (contrato da API).
 */
export async function upsertPurchaseCustomFieldValue(
  apiUrl: string,
  valueData: PurchaseCustomFieldValuePayload
): Promise<void> {
  await upsertCustomFieldValue(
    apiUrl,
    'purchases',
    valueData.purchase_request_id,
    valueData
  );
}
